import KitFadeView from '@omni/kit/components/KitFadeView';
import React, { ReactElement, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, View } from 'react-native';

import PlayerButton, { PlayerButtonSizes } from './PlayerButton';

export interface ShareButtonProps {
  isVisible?: boolean;
  size?: PlayerButtonSizes;
  openShareModal: () => void;
  updateShareModalCoord: (
    _x: number,
    _y: number,
    width: number,
    height: number,
    pageX: number,
    pageY: number
  ) => void;
}

export default function ShareButton({
  isVisible,
  size = PlayerButtonSizes.small,
  openShareModal,
  updateShareModalCoord,
}: ShareButtonProps): ReactElement {
  const { t } = useTranslation('common');

  const shareButtonRef = useRef<View | null>(null);

  const resize = useCallback(() => {
    if (!shareButtonRef.current) return;

    shareButtonRef.current.measure(updateShareModalCoord);
  }, [updateShareModalCoord]);

  useEffect(() => {
    window?.addEventListener?.('resize', resize);

    return () => {
      window?.removeEventListener?.('resize', resize);
    };
  }, [resize]);

  const handleLayout = useCallback(
    (events: any) => {
      if (Platform.OS === 'web') {
        const target = events.target || events.nativeEvent.target;
        target.measure(updateShareModalCoord);

        /**
         * Using ref significantly slows down 1st render in native.
         * So, we only use ref for resizing in web.
         */
        shareButtonRef.current = target;
      }
    },
    [updateShareModalCoord]
  );

  return (
    <KitFadeView show={isVisible} keepElementSize>
      <PlayerButton
        size={size}
        icon='share'
        onPress={openShareModal}
        onLayout={handleLayout}
        frameless
        tooltipProps={{
          label: t('share'),
        }}
      />
    </KitFadeView>
  );
}
